import React, { useState, useEffect, useMemo } from "react";
import { FormFooter } from "@/components/FormFooter";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  productosAPI,
  type ProductoGrupo,
  type ProductoSubgrupo,
} from "@/lib/api";
import { previewCodigoFromNombre } from "@/lib/codigoRubroSlug";
import { formatRubrosApiError } from "./rubrosApiErrors";

function subgrupoGrupoId(s: ProductoSubgrupo): number {
  const g = s.grupo ?? s.grupoId;
  return typeof g === "number" && Number.isFinite(g) ? g : 0;
}

interface SubrubrosABMProps {
  item: ProductoSubgrupo | null;
  onBack: () => void;
  onSaved?: () => void;
  /** Alineado con el listado: permite elegir rubros inactivos al crear/editar subrubro. */
  includeInactiveGrupos?: boolean;
}

export function SubrubrosABM({
  item,
  onBack,
  onSaved,
  includeInactiveGrupos = false,
}: SubrubrosABMProps) {
  const [nombre, setNombre] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [activo, setActivo] = useState(true);
  const [grupoId, setGrupoId] = useState<number>(0);
  const [grupos, setGrupos] = useState<ProductoGrupo[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [loading, setLoading] = useState(false);

  const previewCodigo = useMemo(
    () => previewCodigoFromNombre(nombre, "subgrupo"),
    [nombre]
  );

  useEffect(() => {
    productosAPI
      .listGrupos({ includeInactive: includeInactiveGrupos })
      .then((g) => setGrupos([...g].sort((a, b) => a.nombre.localeCompare(b.nombre))))
      .catch(() => setGrupos([]));
  }, [includeInactiveGrupos]);

  useEffect(() => {
    if (item) {
      setNombre(item.nombre);
      setDescripcion(item.descripcion ?? "");
      setActivo(item.activo);
      setGrupoId(subgrupoGrupoId(item));
    } else {
      setNombre("");
      setDescripcion("");
      setActivo(true);
      setGrupoId(0);
    }
    setHasChanges(false);
  }, [item]);

  const handleSave = async () => {
    if (!nombre.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }
    if (!grupoId || grupoId <= 0) {
      toast.error("Debe seleccionar el rubro al que pertenece el subrubro");
      return;
    }

    setLoading(true);
    try {
      if (item) {
        await productosAPI.updateSubgrupo(item.codigo, {
          nombre: nombre.trim(),
          descripcion: descripcion.trim() || "",
          activo,
          grupo: grupoId,
        });
        toast.success("Subrubro actualizado");
      } else {
        const created = await productosAPI.createSubgrupo({
          nombre: nombre.trim(),
          descripcion: descripcion.trim() || "",
          activo,
          grupo: grupoId,
        });
        toast.success(`Subrubro creado (${created.codigo})`);
      }
      setHasChanges(false);
      onSaved?.();
      setTimeout(() => onBack(), 400);
    } catch (error: unknown) {
      console.error(error);
      toast.error(formatRubrosApiError(error, "Error al guardar el subrubro"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 mt-2">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="sub-codigo">Código</Label>
          <Input
            id="sub-codigo"
            value={item ? item.codigo : previewCodigo}
            disabled
            className="bg-muted"
            tabIndex={1}
          />
          {!item && (
            <p className="text-xs text-muted-foreground mt-1.5">
              Vista previa desde el nombre. El servidor asigna el código definitivo; si ya existe,
              añade sufijo -2, -3, etc.
            </p>
          )}
        </div>
        <div>
          <Label htmlFor="sub-rubro">Rubro (obligatorio)</Label>
          <select
            id="sub-rubro"
            value={grupoId || ""}
            onChange={(e) => {
              setGrupoId(Number(e.target.value) || 0);
              setHasChanges(true);
            }}
            className="w-full px-4 py-2 border border-input bg-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
            tabIndex={2}
          >
            <option value="">Seleccionar rubro…</option>
            {grupos
              .filter((g) => g.id != null && Number(g.id) > 0)
              .map((g) => (
                <option key={g.codigo} value={String(g.id)}>
                  {g.nombre} ({g.codigo})
                </option>
              ))}
          </select>
        </div>
      </div>

      <div>
        <Label htmlFor="sub-activo" className="block mb-2">
          Activo
        </Label>
        <label className="flex items-center gap-2 text-sm">
          <input
            id="sub-activo"
            type="checkbox"
            checked={activo}
            onChange={(e) => {
              setActivo(e.target.checked);
              setHasChanges(true);
            }}
            className="w-4 h-4 rounded border-input"
          />
          <span>Subrubro visible en listados</span>
        </label>
      </div>

      <div>
        <Label htmlFor="sub-nombre">Nombre</Label>
        <Input
          id="sub-nombre"
          value={nombre}
          onChange={(e) => {
            setNombre(e.target.value);
            setHasChanges(true);
          }}
          placeholder="Nombre del subrubro"
          tabIndex={3}
        />
      </div>

      <div>
        <Label htmlFor="sub-desc">Descripción (opcional)</Label>
        <Input
          id="sub-desc"
          value={descripcion}
          onChange={(e) => {
            setDescripcion(e.target.value);
            setHasChanges(true);
          }}
          placeholder="Notas internas"
          tabIndex={4}
        />
      </div>

      <FormFooter
        onBack={onBack}
        backDisabled={loading}
        backTabIndex={6}
        onSave={handleSave}
        saveDisabled={!hasChanges || loading}
        saveTabIndex={5}
        saving={loading}
      />
    </div>
  );
}
